import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  Artifact,
  BackendArtifact,
  GetReleasesParams,
  Release,
  ReleaseDocumentationData,
  ReleaseProduct,
  ReleasesResponse,
  Response,
} from '../types';
import { ReleaseApiService } from './api/release-api.service';

@Injectable({
  providedIn: 'root',
})
export class ReleaseService {
  private releaseApiService = inject(ReleaseApiService);

  private instruction = new BehaviorSubject<string>('');
  private loading = new BehaviorSubject<boolean>(false);

  documentations = new BehaviorSubject<ReleaseDocumentationData[]>([]);
  artifacts = new BehaviorSubject<Artifact[]>([]);
  backendArtifacts = new BehaviorSubject<BackendArtifact[]>([]);
  releaseId = new BehaviorSubject<number>(0);
  releaseVersion = new BehaviorSubject<string>('');
  releaseNotes = new BehaviorSubject<string>('');

  artifacts$ = this.artifacts.asObservable();
  instruction$ = this.instruction.asObservable();
  releaseVersion$ = this.releaseVersion.asObservable();
  loading$ = this.loading.asObservable();
  backendArtifacts$ = this.backendArtifacts.asObservable();
  releaseNotes$ = this.releaseNotes.asObservable();

  releases(params: GetReleasesParams): Observable<ReleasesResponse> {
    return this.releaseApiService.releases(params).pipe(
      tap(() => this.loading.next(true)),
      map(({ result, ok }) => {
        if (!result || !ok) return {} as any;
        return result;
      }),
      finalize(() => this.loading.next(false)),
    );
  }

  getRelease(id: number): Observable<Response<Release>> {
    return this.releaseApiService.release(id).pipe(
      tap(() => {
        this.loading.next(true);
      }),
      switchMap(({ result, ok }) => {
        if (result && ok) {
          const {
            artifacts,
            instructionContent,
            version,
            documentations,
            id,
            backendArtifacts,
            releaseNotes,
          } = result;
          this.releaseVersion.next(version);
          this.artifacts.next(artifacts);
          this.instruction.next(instructionContent);
          this.releaseId.next(id);
          this.backendArtifacts.next(backendArtifacts);
          this.releaseNotes.next(releaseNotes || '');

          const clearDocumentations = documentations.filter(
            (doc) =>
              doc.documentationFileKey.length &&
              doc.documentationFileName.length,
          );

          this.documentations.next(clearDocumentations);
        }
        return of();
      }),
      finalize(() => this.loading.next(false)),
    );
  }

  products(): Observable<ReleaseProduct[]> {
    return this.releaseApiService.products().pipe(
      map(({ result, ok }) => {
        if (!result || !ok) return [];
        return result;
      }),
    );
  }

  releaseBackendArtifacts(id: number): Observable<BackendArtifact[]> {
    return this.releaseApiService.releaseBackendArtifacts(id).pipe(
      map(({ result }) => {
        this.backendArtifacts.next(result || []);
        return result || [];
      }),
    );
  }
}
